import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { AskFailure, buildReply, streamAsk, type AskError, type AskEvent, type AskTransport } from './client';
import type { Msg } from './types';

const Ctx = createContext<AskTransport | null>(null);

/** Hub (apps/hub) lắp một lần: truyền hàm gửi đã bọc token. Không có provider → `useAsk` báo `disabled`. */
export function AskProvider({ send, children }: { send: AskTransport; children: ReactNode }) {
  return <Ctx.Provider value={send}>{children}</Ctx.Provider>;
}

/** Hub đã lắp đường gọi worker chưa (chưa lắp thì màn dùng dữ liệu mẫu). */
export const useAskAvailable = (): boolean => useContext(Ctx) !== null;

export type AskStatus = 'idle' | 'streaming' | 'error';

export type UseAsk = {
  status: AskStatus;
  error: AskError | null;
  /** Chữ đang stream của câu trả lời hiện tại. */
  draft: string;
  /** Công cụ đang chạy (nếu có). */
  tool: string | null;
  /** Hỏi một câu; trả lời hoàn chỉnh trả qua `onReply`. */
  ask: (history: Msg[], text: string) => void;
  cancel: () => void;
  /** Gửi lại câu cuối của lần `ask` gần nhất. */
  retry: () => void;
};

const toWire = (history: Msg[], text: string) => {
  const out: Array<{ role: 'user' | 'assistant'; text: string }> = [];
  for (const m of history) {
    const t = m.text.trim();
    if (t) out.push({ role: 'me' in m && m.me ? 'user' : 'assistant', text: t });
  }
  out.push({ role: 'user', text });
  return out.slice(-20);
};

/** Client Ask HANA: stream, huỷ, thử lại. `onReply` nhận thẻ trả lời khi xong (hoặc phần đã có khi bị huỷ giữa chừng thì không gọi). */
export function useAsk(onReply: (m: Msg) => void): UseAsk {
  const send = useContext(Ctx);
  const [status, setStatus] = useState<AskStatus>('idle');
  const [error, setError] = useState<AskError | null>(null);
  const [draft, setDraft] = useState('');
  const [tool, setTool] = useState<string | null>(null);
  const ctl = useRef<AbortController | null>(null);
  const last = useRef<{ history: Msg[]; text: string } | null>(null);
  const reply = useRef(onReply);
  reply.current = onReply;

  const cancel = useCallback(() => {
    ctl.current?.abort();
    ctl.current = null;
    setStatus('idle');
    setDraft('');
    setTool(null);
  }, []);
  useEffect(() => () => ctl.current?.abort(), []);

  const run = useCallback(async (history: Msg[], text: string) => {
    ctl.current?.abort();
    const c = new AbortController();
    ctl.current = c;
    last.current = { history, text };
    setError(null);
    setDraft('');
    setTool(null);
    if (!send) { setStatus('error'); setError('disabled'); return; }
    setStatus('streaming');
    let acc = '';
    const cards: Array<Extract<AskEvent, { type: 'card' }>> = [];
    try {
      for await (const ev of streamAsk(send, toWire(history, text), c.signal)) {
        if (ev.type === 'text') { acc += ev.delta; setDraft(acc); }
        else if (ev.type === 'tool') setTool(ev.status === 'start' ? ev.name : null);
        else if (ev.type === 'card') cards.push(ev);
      }
      if (c.signal.aborted) return;
      if (!acc.trim()) throw new AskFailure('unknown');
      reply.current(buildReply(acc, cards, Date.now()));
      setStatus('idle');
    } catch (e) {
      if (c.signal.aborted) return;
      setError(e instanceof AskFailure ? e.code : 'unknown');
      setStatus('error');
    } finally {
      if (ctl.current === c) ctl.current = null;
      setDraft('');
      setTool(null);
    }
  }, [send]);

  const ask = useCallback((history: Msg[], text: string) => { void run(history, text); }, [run]);
  const retry = useCallback(() => { if (last.current) void run(last.current.history, last.current.text); }, [run]);
  return { status, error, draft, tool, ask, cancel, retry };
}
